export type CompetitorGeoPoint = {
  latitude: number;
  longitude: number;
};

const EARTH_RADIUS_METERS = 6_371_000;
const DEFAULT_NEARBY_RADIUS_METERS = 3000;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

export function haversineMeters(
  a: CompetitorGeoPoint,
  b: CompetitorGeoPoint,
): number {
  const dLat = toRadians(b.latitude - a.latitude);
  const dLng = toRadians(b.longitude - a.longitude);
  const lat1 = toRadians(a.latitude);
  const lat2 = toRadians(b.latitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function resolveNearbyRadiusMeters(
  radiusMeters: number | undefined,
): number {
  if (
    radiusMeters != null &&
    Number.isFinite(radiusMeters) &&
    radiusMeters > 0
  ) {
    return radiusMeters;
  }
  return DEFAULT_NEARBY_RADIUS_METERS;
}

/**
 * Coverage shown under the competitor map: at least the Nearby Search
 * radius, expanding to the farthest plotted pin (tracked competitors can
 * sit outside discovery).
 */
export function competitorMapCoverKm(input: {
  own: CompetitorGeoPoint;
  places: readonly CompetitorGeoPoint[];
  nearbyRadiusMeters: number;
}): number {
  const nearbyMeters = resolveNearbyRadiusMeters(input.nearbyRadiusMeters);
  let maxMeters = nearbyMeters;
  for (const place of input.places) {
    if (!Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) {
      continue;
    }
    const distance = haversineMeters(input.own, place);
    if (distance > maxMeters) {
      maxMeters = distance;
    }
  }
  return Math.max(1, Math.ceil(maxMeters / 1000));
}

export function offsetByMeters(
  origin: CompetitorGeoPoint,
  northMeters: number,
  eastMeters: number,
): CompetitorGeoPoint {
  const lat =
    origin.latitude + (northMeters / EARTH_RADIUS_METERS) * (180 / Math.PI);
  const cosLat = Math.cos(toRadians(origin.latitude));
  const lng =
    origin.longitude +
    (eastMeters / (EARTH_RADIUS_METERS * Math.max(0.01, cosLat))) *
      (180 / Math.PI);
  return { latitude: lat, longitude: lng };
}

/** Own pin, plotted peers, and the Nearby Search radius around you. */
export function competitorMapCoveragePoints(input: {
  own: CompetitorGeoPoint;
  places: readonly CompetitorGeoPoint[];
  nearbyRadiusMeters?: number;
}): CompetitorGeoPoint[] {
  const radius = resolveNearbyRadiusMeters(input.nearbyRadiusMeters);
  const points: CompetitorGeoPoint[] = [input.own];
  for (const place of input.places) {
    if (!Number.isFinite(place.latitude) || !Number.isFinite(place.longitude)) {
      continue;
    }
    points.push(place);
  }
  points.push(offsetByMeters(input.own, radius, 0));
  points.push(offsetByMeters(input.own, -radius, 0));
  points.push(offsetByMeters(input.own, 0, radius));
  points.push(offsetByMeters(input.own, 0, -radius));
  return points;
}

export function formatCompetitorRadiusHint(input: {
  coverKm: number;
  nearbyRadiusMeters: number;
  radiusHint: string;
  radiusHintTracked: string;
}): string {
  const discoveryKm = Math.max(
    1,
    Math.round(resolveNearbyRadiusMeters(input.nearbyRadiusMeters) / 1000),
  );
  if (input.coverKm > discoveryKm) {
    return input.radiusHintTracked.replace('{km}', String(input.coverKm));
  }
  return input.radiusHint.replace('{km}', String(discoveryKm));
}
